import React from 'react';
import { Image, View } from 'react-native';

import _ from 'lodash';
import moment from 'moment';
import PropTypes from 'prop-types';

import { ErrorScreen, VFText } from '@vfgroup-oneplatform/foundation/Components';
import { useTheme } from '@vfgroup-oneplatform/foundation/Components/Themes';
import CurrentDeviceCard from '@vfgroup-oneplatform/framework/Devices/Components/CurrentDeviceCard';

import PaginatedList from '../../Common/UI/PaginatedList';
import { getThemeImages } from '../../Themes';
import SohoFrameworkTestKeys from '../../Utils/SohoFrameworkTestKeys';

import DevicesLoading from './Components/DevicesLoading';
import styles from './Devices.Styles';

const renderItem = (device, isCurrencyFirst) => {
  const props = getDeviceCardProps(device, isCurrencyFirst);
  return <CurrentDeviceCard {...props} />;
};

const getUpgradeAvailability = ([
  {
    validFor: { endDateTime: EDTContract, startDateTime: SDTContract },
  },
  {
    validFor: { endDateTime: EDTFreeUpgrade, startDateTime: SDTFreeUpgrade },
  },
]) => {
  const formattedSDTFreeUpgrade = moment(SDTFreeUpgrade).format('Do MMM YYYY'),
    formattedTodayDate = moment(new Date()),
    isUpgradeAvailable =
      formattedTodayDate.diff(moment(EDTContract), 'days') <= 0 &&
      formattedTodayDate.diff(moment(SDTContract), 'days') >= 0 &&
      formattedTodayDate.diff(moment(EDTFreeUpgrade), 'days') <= 0 &&
      formattedTodayDate.diff(moment(SDTFreeUpgrade), 'days') >= 0;

  return { formattedSDTFreeUpgrade, isUpgradeAvailable };
};

const getDeviceCardProps = (
  {
    deviceInfo: { brandName: deviceType, modelName: deviceName },
    user: { name },
    productTerm,
    recommendationPrice,
    image,
  },
  isCurrencyFirst,
) => {
  const { isUpgradeAvailable, deviceInfo } = mapDeviceExtraInfo(
    productTerm,
    recommendationPrice,
    isCurrencyFirst,
  );
  return {
    deviceName,
    deviceType,
    withDeviceName: false,
    isUpgradeAvailable,
    primaryButtonTitle: 'soho_devices_upgrade_now',
    image,
    deviceExtraInfo: [{ title: 'soho_devices_assigned_to', subTitle: name }, ...deviceInfo],
    cardContainerStyle: styles.cardContainerStyle(isUpgradeAvailable),
    iconSize: 105,
  };
};

const mapDeviceExtraInfo = (
  productTerm,
  {
    price: {
      taxIncludedAmount: { unit, value },
    },
  },
  isCurrencyFirst,
) => {
  const { formattedSDTFreeUpgrade, isUpgradeAvailable } = getUpgradeAvailability(productTerm);
  let deviceInfo = [
    {
      title: 'soho_devices_upgrade_from',
      subTitle: formattedSDTFreeUpgrade,
      isUpgradeAvailable: false,
    },
  ];

  if (isUpgradeAvailable) {
    deviceInfo = [
      {
        title: 'soho_devices_upgrade_today_for',
        subTitle: isCurrencyFirst ? `${unit}${value?.toFixed(2)}` : `${value?.toFixed(2)}${unit}`,
      },
      {
        title: 'soho_devices_upgrade_from',
        subTitle: formattedSDTFreeUpgrade,
      },
    ];
  }
  return {
    deviceInfo,
    isUpgradeAvailable,
  };
};

const renderEmptyDevices = theme => {
  const sohoImages = getThemeImages();

  return (
    <View style={styles.emptyDeviceContent}>
      <Image
        source={sohoImages.devices_empty}
        testID={SohoFrameworkTestKeys.DevicesEmptyImage}
        style={styles.emptyDevicesImage}
      />
      <VFText
        i18nKey="soho_devices_is_empty"
        style={{ ...styles.headerTitle(theme), ...styles.headerTitleEmptyDevices }}
      />
      <VFText
        i18nKey="soho_devices_you_do_not_have_active_devices_here"
        style={styles.headerSubTitleEmptyDevices}
      />
    </View>
  );
};

const renderDevices = (devices, isCurrencyFirst) => (
  <PaginatedList
    data={devices}
    pageSize={3}
    renderItem={device => renderItem(device, isCurrencyFirst)}
  />
);

const renderError = (error, onTryAgainPress) => {
  return (
    <ErrorScreen
      text={error?.message}
      style={styles.fullScreenError}
      iconStyle={styles.errorIconStyle}
      onPress={onTryAgainPress}
    />
  );
};

const renderContent = ({ devices, isCurrencyFirst, theme, error, isLoading, onTryAgainPress }) => {
  let renderedContent;
  if (error != null) {
    renderedContent = renderError(error, onTryAgainPress);
  } else if (_.isEmpty(devices)) {
    renderedContent = renderEmptyDevices(theme);
  } else {
    renderedContent = renderDevices(devices, isCurrencyFirst);
  }

  return isLoading ? (
    <DevicesLoading theme={theme} />
  ) : (
    <View style={styles.containerStyle}>
      <VFText
        i18nKey="soho_devices_screen_title"
        style={styles.headerTitle(theme)}
        testKey={SohoFrameworkTestKeys.DevicesHeaderTitleText}
      />
      <VFText i18nKey="soho_devices_screen_sub_title" style={styles.headerSubTitle(theme)} />
      {renderedContent}
    </View>
  );
};

function Devices({ isLoading, error, devices, onTryAgainPress, isCurrencyFirst }) {
  const theme = useTheme();

  return renderContent({ devices, isCurrencyFirst, theme, error, isLoading, onTryAgainPress });
}

Devices.defaultProps = {
  isCurrencyFirst: false,
};

Devices.propTypes = {
  devices: PropTypes.array.isRequired,
  isLoading: PropTypes.bool.isRequired,
  error: PropTypes.object,
  isCurrencyFirst: PropTypes.bool,
  onTryAgainPress: PropTypes.func,
};

export default Devices;
